import React, { useEffect, useState } from 'react';
import Select from "react-select";
import { useSelector, useDispatch } from "react-redux";
import { Box, CircularProgress, DialogActions, DialogContent, DialogTitle, Grid, IconButton, Stack, Typography, Card, TextField, FormControl } from '@mui/material';
// icons
import CloseIcon from '@mui/icons-material/Close';
import { grey } from "@mui/material/colors";
// utils and helpers
import { validateRegionData } from "validations/hyperwolf/regions/region";
import { getUnassignedSubRegion } from "../../../redux/slices/hyperwolf/settings";
import { updateRegion, addRegion } from "../../../redux/slices/hyperwolf/settings";
import { closeCustomModalDialog, setModalLoader } from "../../../redux/slices/modalSlice";
import { showToastMsg } from "components/distributionSetting/distributionManagement/addRegionKit";
// custom components
import MDButton from 'components/MDButton';


export default function AddRegion({ closeModal }) {
  const dispatch = useDispatch();

  const { modalData, isLoading = false, isOpen } = useSelector(state => state.modal);
  const { regionId, data = {}, handleRegionsList = () => { } } = modalData;

  const [errors, setErrors] = useState({});
  const [details, setDetails] = useState({ ...data, status: data?.status ?? true, subRegions: data?.subRegions?.map(item => item.subRegionId) || [] });
  const [subRegions, setSubRegions] = useState([]);

  const handleStatusChange = (tab) => {
    setDetails(prev => ({ ...prev, status: tab }))
  }

  const handleSubRegionsList = async () => {
    try {
      const response = await dispatch(getUnassignedSubRegion({ limit: 100, regionId: details?.regionId })).unwrap();
      if (response?.data) {
        setSubRegions(response?.data || [])
      }
    }
    catch (error) {
      showToastMsg(error, "error")
    }
  }

  const handleSave = async () => {
    try {
      if (isLoading) return;
      dispatch(setModalLoader(true))

      const { isValid, myErrors } = validateRegionData(details);
      if (!isValid) {
        setErrors(myErrors);
        return;
      } else {
        setErrors({});
      }

      let response = {};
      if (regionId) {
        response = await dispatch(updateRegion(details)).unwrap();
      } else {
        response = await dispatch(addRegion(details)).unwrap();
      }

      if (response?._id || response?.message) {
        showToastMsg(response?.message || "Success", "success");
        dispatch(closeCustomModalDialog());
        handleRegionsList()
      }
    } catch (error) {
      showToastMsg(error, "error")
    } finally {
      dispatch(setModalLoader(false))
    }
  };

  useEffect(() => {
    if (isOpen) {
      handleSubRegionsList();
    }
  }, [isOpen])

  return (
    <Card sx={{ py: 3, px: 4, borderRadius: 0 }} className="add-region-modal">
      <DialogTitle id='draggable-dialog-title' sx={{ p: 0, mb: 2 }} display='flex' alignItems='center' justifyContent='space-between'>
        {!regionId ? 'Add' : 'Update'} Region
        <IconButton onClick={closeModal} edge='end' size='small'>
          <CloseIcon fontSize='medium' />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 0, mb: 0 }}>
        <Grid spacing={4} container>
          <Grid item xs={12} lg={12}>
            <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} justifyContent="space-between" gap={2} mb={3}>
              <Box display="flex" alignItems="center" gap={1.25}>
                <Typography variant='h5' fontWeight="semibold" color="secondary">Status</Typography>
                <Stack direction="row" alignItems="center" gap={.75} lineHeight={1} p={.75} borderRadius={2} bgcolor="#F2F3F7">
                  <MDButton
                    sx={{ minWidth: '92px', borderRadius: 1.75, minHeight: '28px', p: .25, fontWeight: 500 }}
                    type='button'
                    variant={details.status ? 'contained' : 'text'}
                    color={details.status ? 'success' : 'dark'}
                    size="small"
                    onClick={() => handleStatusChange(true)}
                  >
                    Active
                  </MDButton>
                  <MDButton
                    sx={{ minWidth: '92px', borderRadius: 1.75, minHeight: '28px', p: .25, fontWeight: 500 }}
                    type='button'
                    variant={!details.status ? 'contained' : 'text'}
                    color={!details.status ? 'error' : 'dark'}
                    size="small"
                    onClick={() => handleStatusChange(false)}
                  >
                    Inactive
                  </MDButton>
                </Stack>
              </Box>
            </Stack>

            <Grid container alignItems="center" spacing={2.5}>
              <Grid item xs={12}>
                <Typography variant='body2' fontWeight="medium" color={grey[600]} mb={1}>Region Name</Typography>
                <TextField
                  fullWidth
                  error={Boolean(errors.region)}
                  helperText={errors.region}
                  value={details.region || ""}
                  placeholder="Enter Region Name"
                  onChange={e => {
                    const { value } = e.target;
                    setDetails(prev => ({ ...prev, region: value }))
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant='body2' fontWeight="medium" color={grey[600]} mb={1}>Add Sub Region</Typography>
                <FormControl fullWidth>
                  <Select
                    className='cus-react-select white-radius-field'
                    classNamePrefix="form-select"
                    name="subRegions"
                    isMulti
                    menuPortalTarget={document.body}
                    value={
                      Array.isArray(details?.subRegions)
                        ? subRegions
                          ?.filter(s => (details?.subRegions || []).includes(s._id))
                          ?.map(s => ({ value: s._id, label: s.name }))
                        : []
                    }
                    onChange={options => {
                      const selected = options ? options.map(opt => opt.value) : [];
                      setDetails(prev => ({ ...prev, subRegions: selected }));
                    }}
                    options={
                      subRegions?.map(s => ({ value: s._id, label: s.name }))
                    }
                    placeholder="Select Sub Region(s)"
                    components={{ IndicatorSeparator: null }}
                    styles={{
                      menuPortal: base => ({ ...base, zIndex: 9999 }),
                      placeholder: (provided) => ({ ...provided, textTransform: "unset !important" }),
                    }}
                  />
                  {errors?.subRegions && <p className="error">{errors?.subRegions}</p>}
                </FormControl>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 0, my: 2 }} style={{ justifyContent: "start" }}>
        <MDButton
          onClick={handleSave}
          sx={{ minWidth: '160px', height: "44px" }}
          color="primary"
          type='button'
          variant='contained'
        >
          {isLoading ? <CircularProgress size={16} color='light' /> : ' Save'}
        </MDButton>
        <MDButton
          onClick={closeModal}
          sx={{ minWidth: '160px', height: "44px" }}
          type='button'
          variant='outlined'
          color='dark'
        >
          Cancel
        </MDButton>
      </DialogActions>
    </Card>
  );
}